import { chain } from 'icepick';
import queryString from 'query-string';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import type { Location } from '@atlassian/jira-router';
import type {
	IncrementPlanningSettings,
	IncrementPlanningSettingsState,
	Panels,
	PanelState,
} from '../types';
import { QUERY_PARAM_EPICS, QUERY_PARAM_VERSIONS, QUERY_PARAM_UNSCHEDULED_WORK } from './types';

export { useSendAnalyticsEvent } from './hooks';

export const parseSearchQuery = (
	search: string,
): Record<(typeof Panels)[keyof typeof Panels], boolean> => {
	const searchParams = queryString.parse(search);

	return {
		EPIC_PANEL: searchParams[QUERY_PARAM_EPICS] === 'visible',
		VERSION_PANEL: searchParams[QUERY_PARAM_VERSIONS] === 'visible',
	};
};

export const setPanels = (panels: PanelState, location: Location) => {
	const search = chain({
		...queryString.parse(location.search),
	});

	search.unset(QUERY_PARAM_EPICS);
	search.unset(QUERY_PARAM_VERSIONS);

	if (panels.EPIC_PANEL) {
		search.set(QUERY_PARAM_EPICS, 'visible');
	}

	if (panels.VERSION_PANEL) {
		search.set(QUERY_PARAM_VERSIONS, 'visible');
	}

	return {
		...location,
		search: queryString.stringify(search.value()),
	};
};

export const parseSearchQueryForIPBoard = (
	search: string,
): Record<
	(typeof IncrementPlanningSettings)[keyof typeof IncrementPlanningSettings],
	boolean | undefined
> => {
	const searchParams = queryString.parse(search);

	return {
		SHOW_UNSCHEDULED_COLUMN:
			searchParams[QUERY_PARAM_UNSCHEDULED_WORK] === 'hidden' ? false : undefined,
	};
};

export const setIncrementPlanningSettings = (
	incrementPlanningSettings: IncrementPlanningSettingsState,
	location: Location,
) => {
	const search = chain({
		...queryString.parse(location.search),
	});

	search.unset(QUERY_PARAM_UNSCHEDULED_WORK);

	if (!incrementPlanningSettings.SHOW_UNSCHEDULED_COLUMN) {
		search.set(QUERY_PARAM_UNSCHEDULED_WORK, 'hidden');
	}

	return {
		...location,
		search: queryString.stringify(search.value()),
	};
};

// eslint-disable-next-line jira/ff/inline-usage
export const isViewSettingsPanelAndTailoredViewExperimentEnabled = () =>
	expValEquals('jsw_additional_card_fields_in_view_settings', 'cohort', 'variation');
